'use client';

import React, {
  createContext,
  useContext,
  type ReactNode,
  useEffect,
  useMemo,
} from 'react';
import {useAuth as useOidcAuth} from 'react-oidc-context';
import {useAuth} from '@/matic/hooks/useAuth';
import {getUserManager} from '@/matic/lib/auth/user-manager';
import type {AuthUser} from '@/matic/types/auth';
import {type UserRole} from '@/matic/types/roles';

/**
 * Application context value
 */
type AppContextValue = {
  user: AuthUser | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  tenantId: string | undefined;
  userId: string | undefined;
  userRole: UserRole | undefined;
};

const AppContext = createContext<AppContextValue | undefined>(undefined);

/**
 * Unified application provider for auth and tenant context.
 *
 * Provides a stable context that doesn't change during background session refreshes,
 * preventing unwanted re-renders and query invalidations.
 */
export function AppProvider({children}: {children: ReactNode}) {
  const auth = useAuth();
  const lastGoodUser = React.useRef(auth.user);

  // Proactive session-expiry safety net.
  //
  // Belt-and-suspenders for the UserManager's `accessTokenExpired` wiring: the
  // library's own expiry timer is a `setTimeout` that can be suspended while the
  // tab sleeps and fire late, leaving a window where the UI still trusts a dead
  // token. Re-arm our own timer at the token's expiry; when it fires we re-read
  // the freshest stored token and, only if it is genuinely past expiry (i.e.
  // `automaticSilentRenew` did not land a new one), drop the session so the
  // route guard redirects to login. A successful renew updates `expires_at`,
  // which re-runs this effect and reschedules — so a live session is never cut.
  const oidcExpiresAt = useOidcAuth().user?.expires_at;
  useEffect(() => {
    if (!oidcExpiresAt) return;

    const fireInMs = Math.max(0, oidcExpiresAt * 1000 - Date.now());
    const timer = setTimeout(() => {
      void (async () => {
        const manager = getUserManager();
        const current = await manager?.getUser();
        const expiresAtMs = (current?.expires_at ?? 0) * 1000;
        if (!current || expiresAtMs <= Date.now()) {
          await manager?.removeUser();
        }
      })();
    }, fireInMs);

    return () => {
      clearTimeout(timer);
    };
  }, [oidcExpiresAt]);

  // Preserve last known good user during refreshes
  if (!auth.isLoading && auth.user) {
    lastGoodUser.current = auth.user;
  }

  const stableUser = auth.user ?? lastGoodUser.current;

  const value: AppContextValue = useMemo(
    () => ({
      isLoading: auth.isLoading,
      isAuthenticated: auth.isAuthenticated,
      user: stableUser,
      tenantId: stableUser?.tenantId,
      userId: stableUser?.id,
      userRole: stableUser?.role,
    }),
    [auth.isLoading, auth.isAuthenticated, stableUser]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/**
 * Hook to access the unified app context.
 * Must be used within AppProvider.
 */
export function useAppContext(): AppContextValue {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within AppProvider');
  }

  return context;
}
